import type React from 'react';
import hotkey from '@atlas/tool/box/event/hotkey';
import { Point, Range, Transforms, Editor } from 'slate';
import getNode from '@atlas/tool/box/slate/getNode';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import type { SlateBlock } from '@atlas/renderer';
import isVoidBlock from '@atlas/tool/box/slate/isVoidBlock';

export default function handleArrowKeyDownEvent(editor: Editor, event: React.KeyboardEvent): boolean {
    if (!editor.selection) return false;

    const isUpHotkey = upHotkey.matches(event);
    const isDownHotkey = downHotkey.matches(event);

    if (!isUpHotkey && !isDownHotkey) return false;

    if (!Range.isCollapsed(editor.selection)) return false;

    const { focus } = editor.selection;
    if (focus.offset !== 0) return false;

    const topLevelBlock = getNode<SlateBlock>(editor, [focus.path[0]]);
    if (!topLevelBlock) return false;

    if (focus.path[0] === 0) {
        if (isUpHotkey) {
            const isAtTheBeginningOfText =
                Math.max(...(isCalloutBlock(topLevelBlock) ? focus.path.slice(2) : focus.path)) !== 0;
            if (isAtTheBeginningOfText) return false;

            if (topLevelBlock.type === 'paragraph') return false;

            event.preventDefault();
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: [0] });
            const start: Point = { path: [0, 0], offset: 0 };
            Transforms.setSelection(editor, { anchor: start, focus: start });

            return true;
        }

        return false;
    }

    if (isVoidBlock(topLevelBlock)) {
        // Slate doesn't by default move cursor using up and down arrow keys from void blocks

        if (isUpHotkey) {
            event.preventDefault();
            const endOfPrevBlock = Editor.end(editor, [focus.path[0] - 1]);
            Transforms.setSelection(editor, { anchor: endOfPrevBlock, focus: endOfPrevBlock });
            return true;
        }

        if (isDownHotkey) {
            event.preventDefault();
            const startOfNextBlock = Editor.start(editor, [focus.path[0] + 1]);
            Transforms.setSelection(editor, { anchor: startOfNextBlock, focus: startOfNextBlock });
            return true;
        }
    }

    return false;
}

// #region Helpers

const upHotkey = hotkey('up', 'hidden');
const downHotkey = hotkey('down', 'hidden');

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleArrowKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
